import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Users, KeyRound, ArrowRightLeft, Copy, ShieldCheck, Building2, UserCheck, UserX, Inbox, Sparkles, Loader2, PlayCircle } from "lucide-react";
import SuperAdminPlatform from "./SuperAdminPlatform";

export default function SchoolAdmin() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [pending, setPending] = useState([]);
  const [schools, setSchools] = useState([]); // for target select
  const [xfer, setXfer] = useState({ student_email: "", target_school_id: "" });
  const [issuedKey, setIssuedKey] = useState(null); // {transfer_id, handshake_key, target_school, records_count}
  const [accept, setAccept] = useState({ transfer_id: "", handshake_key: "" });

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
    if (!loading && user && user.role !== "school_admin" && user.role !== "super_admin") {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user?.school_id) return;
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.school_id]);

  const refreshAll = async () => {
    try {
      const [m, t, p] = await Promise.all([
        api.get(`/schools/${user.school_id}/members`),
        api.get(`/transfers/pending`),
        api.get(`/schools/${user.school_id}/pending`),
      ]);
      setMembers(m.data);
      setTransfers(t.data);
      setPending(p.data);
    } catch (e) { console.error(e); }
  };

  const approveMember = async (uid, role) => {
    try {
      await api.post(`/schools/${user.school_id}/approve`, { user_id: uid, role });
      toast.success(`Approved as ${role}`);
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const rejectMember = async (uid) => {
    try {
      await api.post(`/schools/${user.school_id}/reject`, { user_id: uid });
      toast.success("Request rejected");
      refreshAll();
    } catch (e) { toast.error("Failed"); }
  };

  const updateRole = async (uid, role) => {
    try {
      await api.post(`/schools/${user.school_id}/role`, { user_id: uid, role });
      toast.success("Role updated");
      refreshAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const initiateTransfer = async () => {
    if (!xfer.student_email || !xfer.target_school_id) return toast.error("Email & target required");
    try {
      const { data } = await api.post("/transfers/initiate", xfer);
      setIssuedKey(data);
      setXfer({ student_email: "", target_school_id: "" });
      toast.success("Transfer initiated — share the key with the target school");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    }
  };

  const acceptTransfer = async () => {
    if (!accept.transfer_id || !accept.handshake_key) return toast.error("ID & key required");
    try {
      const { data } = await api.post("/transfers/accept", accept);
      toast.success(`Accepted — ${data.records_copied.progress} progress + ${data.records_copied.logbook} log entries copied`);
      setAccept({ transfer_id: "", handshake_key: "" });
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    }
  };

  if (!user || !user.school) {
    if (user?.role === "super_admin") {
      return <SuperAdminPlatform />;
    }
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <Building2 className="w-10 h-10 text-[var(--brand-primary)] mx-auto" />
        <div className="hud-label text-[var(--brand-primary)] mt-4">NO TENANT</div>
        <h2 className="font-heading text-3xl font-bold uppercase tracking-tight mt-2">Set up a school first</h2>
        <p className="text-slate-600 mt-3">Go to the dashboard to create or join a school.</p>
        <Link to="/dashboard"><Button className="mt-6 bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm">Dashboard</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="hud-label text-[var(--brand-primary)]">ADMIN.CONSOLE</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight mt-2">{user.school.name}</h1>
          <div className="mt-3 flex items-center gap-4 text-slate-600 font-mono text-sm">
            <span>CODE: <span className="text-[var(--brand-primary)]">{user.school.join_code}</span></span>
            <button onClick={() => { navigator.clipboard.writeText(user.school.join_code); toast.success("Code copied"); }} className="text-slate-500 hover:text-[var(--brand-primary)]" data-testid="copy-code-btn">
              <Copy className="w-3 h-3" />
            </button>
            <span>· PLAN: {user.school.plan?.toUpperCase()}</span>
          </div>
        </div>
        <div className="hud-label text-slate-500 font-mono">
          TENANT_ID: <span className="text-[var(--brand-primary)]">{user.school_id}</span>
        </div>
      </div>

      <Tabs defaultValue={pending.length ? "pending" : "members"} className="mt-10">
        <TabsList className="bg-slate-50 border border-slate-200 rounded-sm">
          <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wide rounded-sm" data-testid="tab-pending">
            <Inbox className="w-4 h-4 mr-1" /> Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="members" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wide rounded-sm" data-testid="tab-members">
            <Users className="w-4 h-4 mr-1" /> Members
          </TabsTrigger>
          <TabsTrigger value="transfer" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wide rounded-sm" data-testid="tab-transfer">
            <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer
          </TabsTrigger>
          <TabsTrigger value="incoming" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wide rounded-sm" data-testid="tab-incoming">
            <ShieldCheck className="w-4 h-4 mr-1" /> Incoming ({transfers.length})
          </TabsTrigger>
          {user.role === "super_admin" && (
            <TabsTrigger value="avatar" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wide rounded-sm" data-testid="tab-avatar">
              <Sparkles className="w-4 h-4 mr-1" /> Avatar Studio
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <div className="instrument-panel p-6">
            <div className="hud-label text-[var(--brand-primary)]">JOIN.REQUESTS · {pending.length}</div>
            <p className="text-slate-600 text-sm mt-2">Approve cadets who entered your school code. School admins can assign Student or Instructor on approval. Only a super admin can promote someone to School Admin.</p>
            <div className="mt-5 space-y-2">
              {pending.length === 0 && <div className="text-slate-500 text-sm py-6 text-center">No pending requests right now.</div>}
              {pending.map((p) => (
                <div key={p.user_id} className="instrument-panel p-4 flex flex-wrap items-center gap-4 border-slate-200" data-testid={`pending-${p.user_id}`}>
                  {p.picture && <img src={p.picture} alt="" className="w-10 h-10 rounded-sm border border-[var(--brand-primary)]/30" />}
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-heading text-lg text-slate-900" style={{color:"#0F172A"}}>{p.name}</div>
                    <div className="font-mono text-xs text-slate-600">{p.email}</div>
                    {p.pending_requested_at && (
                      <div className="hud-label mt-1">REQUESTED {new Date(p.pending_requested_at).toLocaleDateString()}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => approveMember(p.user_id, "student")} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid={`approve-student-${p.user_id}`}>
                      <UserCheck className="w-4 h-4 mr-1" /> As Student
                    </Button>
                    <Button onClick={() => approveMember(p.user_id, "instructor")} variant="outline" className="border-blue-200 text-[var(--brand-primary)] hover:border-cyan-500 rounded-sm font-heading tracking-wider" data-testid={`approve-instructor-${p.user_id}`}>
                      <UserCheck className="w-4 h-4 mr-1" /> As Instructor
                    </Button>
                    <Button onClick={() => rejectMember(p.user_id)} variant="outline" className="border-red-200 text-red-600 hover:border-rose-500 rounded-sm font-heading tracking-wider" data-testid={`reject-${p.user_id}`}>
                      <UserX className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <div className="instrument-panel">
            <table className="w-full" data-testid="members-table">
              <thead>
                <tr className="hud-label border-b border-white/10">
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Role</th>
                  <th className="text-left p-4">Change</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.user_id} className="border-b border-white/5 hover:bg-slate-50">
                    <td className="p-4">{m.name}</td>
                    <td className="p-4 font-mono text-slate-600 text-sm">{m.email}</td>
                    <td className="p-4">
                      <span className="hud-label text-[var(--brand-primary)]">{m.role}</span>
                    </td>
                    <td className="p-4">
                      {m.user_id !== user.user_id && (
                        <Select value={m.role} onValueChange={(v) => updateRole(m.user_id, v)}>
                          <SelectTrigger className="bg-slate-50 border-slate-200 w-40 rounded-sm" data-testid={`role-select-${m.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-50 border-slate-200 text-slate-900 rounded-sm">
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="instructor">Instructor</SelectItem>
                            {user.role === "super_admin" && <SelectItem value="school_admin">School Admin</SelectItem>}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-slate-500">No members yet — share your school code.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="transfer" className="mt-6 space-y-6">
          <div className="instrument-panel p-6">
            <div className="hud-label text-[var(--brand-primary)]">INITIATE.STUDENT.TRANSFER</div>
            <p className="text-slate-600 text-sm mt-2">
              Transfer a student's records to another school. A single-use handshake key is issued.
              The receiving school admin must enter the key to accept. Past records remain with this school as well.
            </p>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="hud-label text-slate-600 mb-1.5 block">Student email</Label>
                <Input value={xfer.student_email} onChange={(e) => setXfer({ ...xfer, student_email: e.target.value })} placeholder="student@example.com" className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="xfer-email-input" />
              </div>
              <div>
                <Label className="hud-label text-slate-600 mb-1.5 block">Target school ID</Label>
                <Input value={xfer.target_school_id} onChange={(e) => setXfer({ ...xfer, target_school_id: e.target.value })} placeholder="sch_xxxxxx" className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="xfer-target-input" />
              </div>
              <div className="flex items-end">
                <Button onClick={initiateTransfer} className="w-full bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="xfer-initiate-btn">
                  <ArrowRightLeft className="w-4 h-4 mr-1" /> Initiate
                </Button>
              </div>
            </div>
          </div>

          {issuedKey && (
            <div className="instrument-panel p-6 hud-border" data-testid="issued-key-card">
              <div className="hud-label text-[var(--brand-primary)]">HANDSHAKE.KEY · SHOW ONCE</div>
              <h3 className="font-heading text-2xl font-bold uppercase tracking-tight mt-2">Share this with the target school</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <KV label="TRANSFER_ID" value={issuedKey.transfer_id} />
                <KV label="TARGET" value={issuedKey.target_school} />
              </div>
              <div className="mt-4 p-4 bg-white border border-[var(--brand-primary)]/30 font-mono break-all relative">
                <div className="hud-label text-[var(--brand-primary)]">KEY</div>
                <div className="text-[var(--brand-primary)] text-lg mt-1">{issuedKey.handshake_key}</div>
                <button onClick={() => { navigator.clipboard.writeText(issuedKey.handshake_key); toast.success("Key copied"); }} className="absolute top-3 right-3 text-slate-600 hover:text-[var(--brand-primary)]" data-testid="copy-handshake-key">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 text-slate-500 text-sm font-mono">
                {issuedKey.records_count.progress} progress entries · {issuedKey.records_count.logbook} log entries · expires in 7 days
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="incoming" className="mt-6 space-y-6">
          <div className="instrument-panel p-6">
            <div className="hud-label text-[var(--brand-primary)]">ACCEPT.WITH.HANDSHAKE</div>
            <p className="text-slate-600 text-sm mt-2">
              Enter the Transfer ID and Handshake Key shared by the sending school's admin.
            </p>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              <Input value={accept.transfer_id} onChange={(e) => setAccept({ ...accept, transfer_id: e.target.value })} placeholder="xfr_xxxxxx" className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="accept-transfer-id" />
              <Input value={accept.handshake_key} onChange={(e) => setAccept({ ...accept, handshake_key: e.target.value })} placeholder="handshake key" className="bg-slate-50 border-slate-200 rounded-sm font-mono" data-testid="accept-key-input" />
              <Button onClick={acceptTransfer} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="accept-transfer-btn">
                <KeyRound className="w-4 h-4 mr-1" /> Accept
              </Button>
            </div>
          </div>

          <div className="instrument-panel p-6">
            <div className="hud-label text-[var(--brand-primary)]">PENDING.INCOMING · {transfers.length}</div>
            <div className="mt-4 space-y-2">
              {transfers.length === 0 && <div className="text-slate-500 text-sm">No pending transfers.</div>}
              {transfers.map((t) => (
                <div key={t.transfer_id} className="p-3 border border-white/10 rounded-sm flex items-center gap-4 font-mono text-sm" data-testid={`pending-${t.transfer_id}`}>
                  <span className="text-[var(--brand-primary)]">{t.transfer_id}</span>
                  <span className="text-slate-600">{t.student_email}</span>
                  <span className="text-slate-500">from {t.source_school_id}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {user.role === "super_admin" && (
          <TabsContent value="avatar" className="mt-6">
            <AvatarStudio />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function AvatarStudio() {
  const LESSONS = [
    { id: "lesson-stalls-001",       title: "Stalls",        module: "Principles of Flight" },
    { id: "lesson-medium-turns-001", title: "Medium Turns",  module: "Principles of Flight" },
    { id: "lesson-steep-turns-001",  title: "Steep Turns",   module: "Advanced Manoeuvres" },
  ];
  const [statuses, setStatuses] = useState({}); // lesson_id -> status payload
  const [starting, setStarting] = useState(null);

  const fetchStatus = async (lessonId) => {
    try {
      const { data } = await api.get(`/admin/lessons/${lessonId}/avatar-studio/status`);
      setStatuses((s) => ({ ...s, [lessonId]: data }));
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    LESSONS.forEach((l) => fetchStatus(l.id));
    const t = setInterval(() => {
      LESSONS.forEach((l) => fetchStatus(l.id));
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startBatch = async (lessonId) => {
    setStarting(lessonId);
    try {
      await api.post(`/admin/lessons/${lessonId}/avatar-studio/start`);
      toast.success("Avatar generation kicked off — leave this page open, it'll keep running");
      await fetchStatus(lessonId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    } finally { setStarting(null); }
  };

  const syncFromStorage = async (lessonId) => {
    setStarting(lessonId);
    try {
      const { data } = await api.post(`/admin/lessons/${lessonId}/avatar-studio/sync-from-storage`);
      const parts = [];
      if (data.cover) parts.push("cover");
      if (data.backdrop) parts.push("backdrop");
      if (data.blocks_synced?.length) parts.push(`${data.blocks_synced.length} blocks`);
      const synced = parts.length ? parts.join(", ") : "nothing";
      toast.success(`Imported from shared storage: ${synced}`);
      await fetchStatus(lessonId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setStarting(null); }
  };

  return (
    <div className="space-y-4">
      <div className="instrument-panel p-5">
        <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2"><Sparkles className="w-4 h-4" /> AVATAR STUDIO</div>
        <p className="text-slate-600 text-sm mt-2 max-w-3xl">
          Renders a 1986-style classroom backdrop and a lip-synced talking-head video for every narration block, using the character image + cached narration audio.
          For each narration block we (1) generate a per-block chalkboard starting image with Nano Banana, (2) animate it with Kling 1.6 image-to-video into multiple cinematic clips, (3) concatenate them to match the narration length, and (4) overlay accurate lip-sync with Sync-Lipsync v2. Cached and idempotent.
          Cost guideline: roughly $15–25 per lesson at current fal.ai pricing (depends on narration length).
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {LESSONS.map((l) => {
          const s = statuses[l.id];
          const job = s?.job;
          const total = job?.total || 0;
          const done = job?.done || 0;
          const failed = job?.failed || 0;
          const isRunning = job && job.status === "running";
          const pct = total > 0 ? Math.round(((done + failed) / total) * 100) : 0;
          return (
            <div key={l.id} className="instrument-panel p-5 bracket relative">
              <span className="bl" /><span className="br" />
              <div className="hud-label" style={{ color: "#FF2A6D" }}>{l.module}</div>
              <div className="font-heading text-2xl uppercase tracking-tight text-slate-900 mt-1">{l.title}</div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="instrument-panel p-2">
                  <div className="hud-label">CLASSROOM</div>
                  <div className={`font-mono mt-1 ${s?.classroom_backdrop ? "text-green-400" : "text-slate-500"}`}>{s?.classroom_backdrop ? "READY" : "—"}</div>
                </div>
                <div className="instrument-panel p-2">
                  <div className="hud-label">BLOCKS LIP-SYNCED</div>
                  <div className="font-mono mt-1 text-[var(--brand-primary)]">{s?.ready?.length || 0}</div>
                </div>
              </div>

              {isRunning && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-[var(--brand-primary)] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> RENDERING</span>
                    <span className="text-[var(--brand-primary)]">{done + failed} / {total} · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 mt-2 overflow-hidden">
                    <div className="h-full bg-amber-500 transition-[width]" style={{ width: `${pct}%` }} />
                  </div>
                  {failed > 0 && <div className="hud-label text-red-600 mt-2">{failed} FAILED</div>}
                </div>
              )}

              <div className="mt-5 space-y-2">
                <Button
                  onClick={() => startBatch(l.id)}
                  disabled={!!starting || isRunning}
                  className="w-full bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm"
                  data-testid={`avatar-gen-${l.id}`}
                >
                  {isRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> :
                   starting === l.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> :
                   <><PlayCircle className="w-4 h-4 mr-2" /> {s?.ready?.length ? "Re-Generate" : "Generate"}</>}
                </Button>
                <Button
                  onClick={() => syncFromStorage(l.id)}
                  disabled={!!starting || isRunning}
                  variant="outline"
                  className="w-full border-blue-200 text-[var(--brand-primary)] hover:bg-blue-50 font-heading tracking-wider rounded-sm"
                  data-testid={`avatar-sync-${l.id}`}
                  title="Import cover, backdrop and avatar videos from shared object storage. $0 — no rendering. Use to promote work from preview to production."
                >
                  ↡ Sync from Storage
                </Button>
                {s?.ready?.length > 0 && !isRunning && (
                  <div className="hud-label text-green-400 mt-2 text-center">{s.ready.length} BLOCKS READY</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="hud-label">{label}</div>
      <div className="font-mono text-[var(--brand-primary)] text-sm break-all mt-1">{value}</div>
    </div>
  );
}
